import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CheckIcon from "../../../../../../assets/TailoredGeneration/icons/tg-check.svg";
import XIcon from "../../../../../../assets/TailoredGeneration/icons/tg-x.svg";
import bad1 from "../../../../../../assets/TailoredGeneration/images/examples/bad_dataset_examples/bad_dataset_example1.png";
import bad2 from "../../../../../../assets/TailoredGeneration/images/examples/bad_dataset_examples/bad_dataset_example2.png";
import bad3 from "../../../../../../assets/TailoredGeneration/images/examples/bad_dataset_examples/bad_dataset_example3.png";
import bad4 from "../../../../../../assets/TailoredGeneration/images/examples/bad_dataset_examples/bad_dataset_example4.png";
import bad5 from "../../../../../../assets/TailoredGeneration/images/examples/bad_dataset_examples/bad_dataset_example5.png";
import bad6 from "../../../../../../assets/TailoredGeneration/images/examples/bad_dataset_examples/bad_dataset_example6.png";
import good1 from "../../../../../../assets/TailoredGeneration/images/examples/good_dataset_examples/good_dataset_example1.jpg";
import good2 from "../../../../../../assets/TailoredGeneration/images/examples/good_dataset_examples/good_dataset_example2.jpg";
import good3 from "../../../../../../assets/TailoredGeneration/images/examples/good_dataset_examples/good_dataset_example3.jpg";
import good4 from "../../../../../../assets/TailoredGeneration/images/examples/good_dataset_examples/good_dataset_example4.png";
import good5 from "../../../../../../assets/TailoredGeneration/images/examples/good_dataset_examples/good_dataset_example5.jpg";
import good6 from "../../../../../../assets/TailoredGeneration/images/examples/good_dataset_examples/good_dataset_example6.jpg";
import image_guideline1 from "../../../../../../assets/TailoredGeneration/images/examples/image_guidelines/guideline-1.png";
import image_guideline2 from "../../../../../../assets/TailoredGeneration/images/examples/image_guidelines/guideline-2.png";
import image_guideline3 from "../../../../../../assets/TailoredGeneration/images/examples/image_guidelines/guideline-3.png";
import image_guideline4 from "../../../../../../assets/TailoredGeneration/images/examples/image_guidelines/guideline-4.png";
import styles from "./TgDatasetTrainingGuide.module.scss";

type Props = {
	baseTileWidth?: number;
	baseTileHeight?: number;
};

const TgDatasetTrainingGuide = ({ baseTileWidth, baseTileHeight }: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.trainingGuide" });
	const goodImages: string[] = [good1, good2, good3, good4, good5, good6];
	const badImages: string[] = [bad1, bad2, bad3, bad4, bad5, bad6];

	const baseW = baseTileWidth ?? 192;
	const baseH = baseTileHeight ?? 166;

	const [scaledW, setScaledW] = useState<number>(baseW);

	useEffect(() => {
		const compute = () => {
			const vw = typeof window !== "undefined" ? window.innerWidth : 1920;
			if (vw <= 1920) {
				setScaledW(baseW);
			} else {
				const scale = Math.min(1.55, vw / 1920);
				setScaledW(Math.round(baseW * scale));
			}
		};
		compute();
		window.addEventListener("resize", compute);
		return () => window.removeEventListener("resize", compute);
	}, [baseW]);

	const gridStyle = { gridTemplateColumns: `repeat(3, ${scaledW}px)` } as React.CSSProperties;
	const itemStyle = { aspectRatio: `${baseW} / ${baseH}` } as React.CSSProperties;
	const baseGuideW = baseW;
	const baseGuideH = baseH;
	const guideScaledW = Math.round(scaledW * 1.54);
	const gridGuidesStyle = { gridTemplateColumns: `repeat(2, ${guideScaledW}px)` } as React.CSSProperties;
	const itemGuideStyle = { aspectRatio: `${baseGuideW} / ${baseGuideH}` } as React.CSSProperties;

	return (
		<Box className={styles.container}>
			<Typography className={styles.title}>{t("title")}</Typography>
			<Typography className={styles.description}>{t("description")}</Typography>

			<Box className={styles.section}>
				<Box className={styles.sectionHeader}>
					<img src={CheckIcon} width={20} height={20} />
					<Typography className={styles.sectionTitle}>{t("goodDatasetTitle")}</Typography>
				</Box>
				<Typography className={styles.sectionDescription}>{t("goodDatasetDescription")}</Typography>
				<Box className={styles.grid} style={gridStyle}>
					{goodImages.map((src, idx) => (
						<Box key={idx} className={styles.gridItem} style={itemStyle}>
							<img className={styles.gridImage} src={src} alt={`good-${idx + 1}`} />
						</Box>
					))}
				</Box>
			</Box>

			<Box className={styles.section}>
				<Box className={styles.sectionHeader}>
					<img src={XIcon} width={20} height={20} />
					<Typography className={styles.sectionTitle}>{t("badDatasetTitle")}</Typography>
				</Box>
				<Typography className={styles.sectionDescription}>{t("badDatasetDescription")}</Typography>
				<Box className={styles.grid} style={gridStyle}>
					{badImages.map((src, idx) => (
						<Box key={idx} className={styles.gridItem} style={itemStyle}>
							<img className={styles.gridImage} src={src} alt={`bad-${idx + 1}`} />
						</Box>
					))}
				</Box>
			</Box>

			<Box className={styles.section}>
				<Box className={styles.sectionHeader}>
					<Typography className={styles.sectionTitle}>{t("imageGuidelinesTitle")}</Typography>
				</Box>
				<Box className={styles.grid} style={gridGuidesStyle}>
					<Box>
						<Box className={styles.gridItem} style={itemGuideStyle}>
							<img className={styles.gridImage} src={image_guideline1} alt={"guideline-1"} />
						</Box>
						<Box className={styles.guidelineCaption}>
							<img src={CheckIcon} width={16} height={16} />
							<Typography className={styles.guidelineText}>{t("guideline1")}</Typography>
						</Box>
					</Box>
					<Box>
						<Box className={styles.gridItem} style={itemGuideStyle}>
							<img className={styles.gridImage} src={image_guideline2} alt={"guideline-2"} />
						</Box>
						<Box className={styles.guidelineCaption}>
							<img src={XIcon} width={16} height={16} />
							<Typography className={styles.guidelineText}>{t("guideline2")}</Typography>
						</Box>
					</Box>
					<Box>
						<Box className={styles.gridItem} style={itemGuideStyle}>
							<img className={styles.gridImage} src={image_guideline3} alt={"guideline-3"} />
						</Box>
						<Box className={styles.guidelineCaption}>
							<img src={CheckIcon} width={16} height={16} />
							<Typography className={styles.guidelineText}>{t("guideline3")}</Typography>
						</Box>
					</Box>
					<Box>
						<Box className={styles.gridItem} style={itemGuideStyle}>
							<img className={styles.gridImage} src={image_guideline4} alt={"guideline-4"} />
						</Box>
						<Box className={styles.guidelineCaption}>
							<img src={XIcon} width={16} height={16} />
							<Typography className={styles.guidelineText}>{t("guideline4")}</Typography>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default TgDatasetTrainingGuide;
